import * as FBAuth from 'firebase/auth';
import { auth } from './config/Firebase';
import { Analytics } from './data/Analytics';
import { Environment } from './util/Environment';
import { Log } from './util/Logging';

const googleProvider = new FBAuth.GoogleAuthProvider();

export async function signInWithEmail(email: string, password: string): Promise<FBAuth.User> {
	const result = await FBAuth.signInWithEmailAndPassword(auth, email, password);
	Analytics.track('login', { method: 'email' });
	return result.user;
}

export async function signInWithGoogle(): Promise<FBAuth.User> {
	const result = await FBAuth.signInWithPopup(auth, googleProvider);
	Analytics.track('login', { method: 'google' });
	return result.user;
}

export async function sendPasswordReset(email: string): Promise<void> {
	await FBAuth.sendPasswordResetEmail(auth, email);
	Analytics.track('password_reset_requested');
}

export async function signOut(): Promise<void> {
	Analytics.track('logout');
	await FBAuth.signOut(auth);
}

export async function isAdmin(user: FBAuth.User): Promise<boolean> {
	const idTokenResult = await user.getIdTokenResult();
	Log.system.debug('idTokenResult.claims.admin', idTokenResult.claims.admin);
	return idTokenResult.claims.admin === true;
}

export function errorMessageForAuthError(error: unknown): string {
	if (isAuthError(error)) {
		Log.system.warn(String(error));

		switch (error.code) {
		case 'auth/admin-restricted-operation':
			return `No account found. An existing ${Environment.gameName} account is needed to sign in.`;
		case 'auth/invalid-credential':
			return 'Account not found or password is incorrect';
		case 'auth/wrong-password':
			return 'Incorrect password. Please try again.';
		case 'auth/weak-password':
			return 'Password is too weak. Please use a stronger password.';
		case 'auth/email-already-in-use':
			return 'An account with this email already exists.';
		case 'auth/invalid-email':
			return 'Invalid email address.';
		case 'auth/user-disabled':
			return 'This account has been disabled.';
		case 'auth/too-many-requests':
			return 'Too many failed attempts. Please try again later.';
		default:
			if ('message' in error) {
				return String(error.message);
			} else {
				return String(error);
			}
		}
	} else {
		return 'Authentication failed. Please try again.';
	}
}

function isAuthError(error: unknown): error is FBAuth.AuthError {
	return (typeof error === 'object') && (error !== null) && ('code' in error);
};